/* eslint-disable @typescript-eslint/ban-types */

import Echo, {Channel} from 'laravel-echo';
import Pusher, {Options} from 'pusher-js';
import {PusherConnector} from 'laravel-echo/dist/connector';
import Dispatcher from 'pusher-js/types/src/core/events/dispatcher';
import {AuthOptionsT} from 'pusher-js/src/core/auth/options';
import app from '@src/helpers/app';
import {HttpEvents} from '@src/@types/services/Http/Http';
import {IEvent, IListener} from '@src/@interfaces/services/Events';
import {IClient} from '@src/@interfaces/services/WebSocket/IClient';
import {ISubscription} from '@src/@interfaces/services/Events/ISubscription';
import {IReduxWebSocketServiceState} from '@src/@interfaces';
import {websocketSetConnectionState, websocketSetErrorMessage} from '@src/_redux/services/websocket/actions';
import {IChannel} from '@src/@interfaces/services/WebSocket/IChannel';

export enum WSEvents {
    CONNECTION_STATE_CHANGED = 'ws@connection-state-changed',
    CONNECT = 'ws@connect',
    CONNECTED = 'ws@connected',
    DISCONNECT = 'ws@disconnect',
    DISCONNECTED = 'ws@disconnected',
}

type TConnector = Omit<PusherConnector, 'pusher'> & {
    pusher: Pusher;
}

type States = IReduxWebSocketServiceState['connectionState'];

let lastDispatchedEvent: any = null;

export default class Client extends Echo implements IClient {
    private readyToConnect = false;
    private subscribed = false;
    private terminating = false;

    connector!: TConnector;

    constructor(options: any) {
        super(options);

        this.onConnectionStateChangedListener = this.onConnectionStateChangedListener.bind(this);
        this.onConnectionEventListener = this.onConnectionEventListener.bind(this);
        this.onMessageReceivedListener = this.onMessageReceivedListener.bind(this);
        this.onErrorListener = this.onErrorListener.bind(this);
    }

    initialize(): void {
        if (this.readyToConnect) {
            return;
        }

        this.readyToConnect = true;
        this.options = this.getDefaultOptions();

        this.connect();
        this.registerPusherInternalListener();

        if (!this.options.withoutInterceptors) {
            this.registerInterceptors();
        }
    }

    connect() {
        if (!this.readyToConnect) {
            return;
        }

        this.events.dispatch(WSEvents.CONNECT, {client: this});
        if (!this.connector) {
            this.emitConnectionStateChange('connecting');
            super.connect();
        } else if (
            !this.connector.pusher.connection.runner ||
            !this.connector.pusher.connection.connection
        ) {
            app().dispatchAction(websocketSetErrorMessage('Can\'t establish connection'));
        } else {
            this.connector.pusher.connect();
        }

        this.addEventListeners();
    }

    disconnect() {
        this.terminating = true;
        this.events.dispatch(WSEvents.DISCONNECT, {client: this});

        super.disconnect();
    }

    registerInterceptors() {
        if (!this.readyToConnect) {
            return;
        }

        super.registerInterceptors();

        app('http').listen<any>(HttpEvents.BEFORE_SEND_REQUEST, (event, payload) => {
            if (this.socketId() && 'options' in payload) {
                payload.options.headers.set('X-Socket-ID', this.socketId());
            }
        });
    }

    channel(channel: string): IChannel {
        return this.decorateChannel(super.channel(channel));
    }

    private(channel: string): IChannel {
        return this.decorateChannel(super.private(channel));
    }

    encryptedPrivate(channel: string): IChannel {
        return this.decorateChannel(super.encryptedPrivate(channel));
    }

    onMessage(channel: string | string[], event: string | string[], listener: IListener<any>): ISubscription {
        return this.listenMessages(this.channel, channel, event, listener);
    }

    onPrivateMessage(channel: string | string[], event: string | string[], listener: IListener<any>): ISubscription {
        return this.listenMessages(this.private, channel, event, listener);
    }

    onEncryptedPrivateMessage(channel: string | string[], event: string | string[], listener: IListener<any>): ISubscription {
        return this.listenMessages(this.encryptedPrivate, channel, event, listener);
    }

    onConnectionStateChanged(listener: IListener<{
        client: IClient,
        state: IReduxWebSocketServiceState['connectionState'],
    }>): ISubscription {
        return this.events.listen(WSEvents.CONNECTION_STATE_CHANGED, listener);
    }

    onConnect(listener: IListener<{ client: IClient }>): ISubscription {
        return this.events.listen(WSEvents.CONNECT, listener);
    }

    onConnected(listener: IListener<{ client: IClient }>): ISubscription {
        return this.events.listen(WSEvents.CONNECTED, listener);
    }

    onDisconnect(listener: IListener<{ client: IClient }>): ISubscription {
        return this.events.listen(WSEvents.DISCONNECT, listener);
    }

    onDisconnected(listener: IListener<{ client: IClient }>): ISubscription {
        return this.events.listen(WSEvents.DISCONNECTED, listener);
    }

    protected listenMessages(
        channelResolver: (name: string) => IChannel,
        channel: string | string[],
        event: string | string[],
        listener: IListener
    ): ISubscription {
        const events = Array.isArray(event) ? event : [event];
        const channels = Array.isArray(channel) ? channel : [channel];

        const eventsToSubscribe = [];
        for (const name of channels) {
            const channelName = channelResolver.call(this, name).name;
            for (const eventName of events) {
                eventsToSubscribe.push(`ws@${channelName}:${eventName}`);
            }
        }

        return this.events.listen(eventsToSubscribe, listener);
    }

    protected getDefaultOptions(): Record<any, any> & Options {
        const config = app('config');
        const headersProvider: AuthOptionsT<any>['headersProvider'] = () => ({
            'X-Requested-With': 'XMLHttpRequest',
            'X-CSRF-TOKEN': app('CSRFManager').CSRFToken,
            'X-XSRF-TOKEN': app('CSRFManager').XSRFToken,
            'Authorization': `Bearer ${app('auth').getAccessToken()}`,
        });

        return {
            ...this.options,
            broadcaster: 'pusher',
            Pusher: Pusher,
            key: config.get('client.broadcasting.app-key'),
            wsHost: config.get('client.broadcasting.host'),
            wsPort: config.get('client.broadcasting.ws-port'),
            wssPort: config.get('client.broadcasting.wss-port'),
            cluster: config.get('client.broadcasting.cluster'),
            forceTLS: config.get('client.broadcasting.scheme', 'http' as string) === 'https',
            auth: {
                headers: {},
            },
            userAuthentication: {
                endpoint: '/broadcasting/user-auth',
                headersProvider,
                headers: {},
            },
            channelAuthorization: {
                endpoint: '/broadcasting/auth',
                headersProvider,
                headers: {},
            },
            encrypted: true,
            disableStats: true,
            enabledTransports: ['ws', 'wss'],
        };
    }

    protected addEventListeners() {
        if (!this.connector?.pusher || this.subscribed) {
            return;
        }

        this.events.listen([
            'pusher.error',
            'pusher.closed',
            'pusher.connecting',
        ], this.onConnectionEventListener);
        this.events.listen('pusher.message', this.onMessageReceivedListener);
        this.events.listen('pusher.state_change', this.onConnectionStateChangedListener);
        this.events.listen(['pusher.error', 'pusher.pusher:subscription_error'], this.onErrorListener);
        this.subscribed = true;
    }

    protected removeEventListeners() {
        if (!this.connector?.pusher || !this.subscribed) {
            return;
        }

        this.events.forget([
            'pusher.error',
            'pusher.closed',
            'pusher.connecting',
        ], this.onConnectionEventListener);
        this.events.forget('pusher.message', this.onMessageReceivedListener);
        this.events.forget('pusher.state_change', this.onConnectionStateChangedListener);
        this.events.forget('pusher.error', this.onErrorListener);
        this.subscribed = false;
    }

    protected onMessageReceivedListener(event: IEvent, payload: {
        data: {
            channel: string,
            event: string,
            data: any,
        },
        metadata?: any,
    }) {
        const eventPayload = payload.data;
        const shouldSkip = lastDispatchedEvent === eventPayload;

        lastDispatchedEvent = eventPayload;

        if (!eventPayload.channel || !eventPayload.event || shouldSkip || eventPayload.event.startsWith('pusher')) {
            return;
        }

        const data = (
            eventPayload.data != null &&
            typeof eventPayload.data === 'object' &&
            !Array.isArray(eventPayload.data)
        ) ? {
            eventName: eventPayload.event,
            ...eventPayload.data,
        } : eventPayload.data;

        this.events.dispatch(`ws@${eventPayload.channel}:${eventPayload.event}`, data);
    }

    protected onConnectionStateChangedListener(event: IEvent, payload: {
        data: { previous: States, current: States },
    }) {
        if (payload.data.current === 'connected') {
            this.events.dispatch(WSEvents.CONNECTED, {client: this});
        } else if (payload.data.current === 'disconnected') {
            this.events.dispatch(WSEvents.DISCONNECTED, {client: this});

            if (this.terminating) {
                this.removeEventListeners();
                this.terminating = false;
            }
        }

        this.emitConnectionStateChange(payload.data.current);
    }

    protected onConnectionEventListener(event: IEvent, payload: any): void {
        const eventToState: Record<string, States> = {
            'pusher.error': 'failed',
            'pusher.closed': 'failed',
            'pusher.connecting': 'connecting',
            'pusher.initialized': 'initialized',
        };

        if (event.name === 'pusher.error' && payload?.data?.type !== 'WebSocketError') {
            return;
        }

        this.emitConnectionStateChange(eventToState[event.name]);
    }

    protected onErrorListener(event: IEvent, payload: {
        data: { type: string, error: any },
        metadata: any,
    }) {
        if (payload.data.type === 'WebSocketError') {
            app().dispatchAction(websocketSetErrorMessage('Cannot establish connection'));

            return;
        }

        console.error(payload.data.type, payload.data.error, payload.metadata);
    }

    protected emitConnectionStateChange(state: States) {
        this.events.dispatch(WSEvents.CONNECTION_STATE_CHANGED, {client: this, state: state});
        app().dispatchAction(websocketSetConnectionState(state));
    }

    protected registerPusherInternalListener(): void {
        if (!this.connector?.pusher?.global_emitter) {
            return;
        }

        const emitter = (this.connector.pusher.global_emitter as any);
        const prototype = (emitter.prototype ?? emitter.__proto__) as Dispatcher;
        if (prototype) {
            const baseEmit = prototype.emit;
            prototype.emit = function (eventName, data, metadata) {
                app('events').dispatch(`pusher.${eventName}`, {data, metadata});

                return baseEmit.call(this, eventName, data, metadata);
            };
        }
    }

    protected decorateChannel(channel: Channel): IChannel {
        const proto = ((item: any) => (item.prototype ?? item.__proto__))(channel);
        proto.stopListeningNotifications = function (this: IChannel, callback: Function) {
            return this.stopListening('.Illuminate\\Notifications\\Events\\BroadcastNotificationCreated', callback);
        };

        channel.error((data: { type: string }) => {
            if (data.type === 'AuthError') {
                this.leave((channel as IChannel).name);
            }
        });

        return channel as IChannel;
    }

    protected get events() {
        return app('events');
    }
}
